import { captureException } from '@sentry/react';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useRef } from 'react';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useGetUserQuery } from 'src/common/slices';
import { useGoogleOneTapAuth } from './hooks/use-google-one-tap-auth';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
interface GoogleAuthError {
  data?: {
    message?: string;
  };
}
export const GoogleOneTap = () => {
  const isAutoLoginOn = useFeatureIsOn('google-one-tap-auto-login');
  const {
    data: user
  } = useGetUserQuery();
  const handleAuth = useGoogleOneTapAuth();
  const isAuthenticatedRef = useRef(false);
  const initIfReady = useCallback(() => {
    const shouldShow = !user && !isAuthenticatedRef.current;
    if (!shouldShow) return;
    const googleId = window.google?.accounts?.id;
    if (!GOOGLE_CLIENT_ID || !googleId) return;
    const nonce = window.crypto?.randomUUID() || Math.random().toString(36).slice(2);
    googleId.initialize({
      client_id: GOOGLE_CLIENT_ID,
      nonce,
      callback: response => {
        if (!response.credential) return;
        handleAuth(response.credential, nonce).then(() => {
          window.dispatchEvent(new CustomEvent('googleOneTapAuthenticated'));
        }).catch((error: GoogleAuthError) => {
          NotificationDispatch('error', error?.data?.message || 'Please try again later or use email/google sign in.');
          captureException(error);
        });
      },
      cancel_on_tap_outside: false,
      auto_select: isAutoLoginOn
    });
    googleId.prompt();
  }, [user, handleAuth, isAutoLoginOn]);
  useEffect(() => {
    const handleAuthEvent = () => {
      isAuthenticatedRef.current = true;
      const googleId = window.google?.accounts?.id;
      // Re-initialize Google One Tap to clear the prompt
      if (googleId && GOOGLE_CLIENT_ID) {
        googleId.cancel?.();
        googleId.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: () => {} // Empty callback since we're just clearing the prompt
        });
      }
    };
    window.addEventListener('googleOneTapAuthenticated', handleAuthEvent);
    return () => {
      window.removeEventListener('googleOneTapAuthenticated', handleAuthEvent);
    };
  }, []);
  useEffect(() => {
    // Attempt immediately
    initIfReady();

    // Also listen for the GSI script load as a signal to retry
    const gsiScript = document.querySelector<HTMLScriptElement>('script[src*="gsi/client"]');
    gsiScript?.addEventListener('load', initIfReady);
    return () => {
      gsiScript?.removeEventListener('load', initIfReady);
      window.google?.accounts?.id?.cancel?.();
    };
  }, [initIfReady]);
  return <div id="google-one-tap-container" style={{
    position: 'fixed',
    top: 0,
    right: 0,
    zIndex: 9999
  }} data-sentry-component="GoogleOneTap" data-sentry-source-file="google-one-tap.tsx" />;
};